import React, { Component } from 'react';
import {
  Box,
  Typography,
  Switch,
  Paper,
  Chip,
  CircularProgress,
  Fade,
  Stack
} from '@mui/material';

import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import Title from '../resources/Title';

import * as Constants from '../api/constants'

export default class AdminSettingsSVLink extends Component {

  state = {
    loading: true,
    saving: false,
    status: false,
    onDateTime: null,
    offDateTime: null
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    try {
      this.setState({ loading: true });

      const response = await fetch(
        Constants.baseURL + Constants.apiGetSvLinkStatus,
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load');
      }

      const data = await response.json();

      this.setState({
        status: data.status,
        onDateTime: data.onDateTime,
        offDateTime: data.offDateTime,
        loading: false
      });

    } catch (e) {
      console.error(e);
      this.setState({ loading: false });
    }
  };

  updateStatus = async (checked) => {
    try {

      this.setState({
        saving: true,
        status: checked
      });

      const response = await fetch(
        Constants.baseURL + Constants.apiUpdateSVLinkStatus,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            status: checked
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update');
      }

      const statusResponse = await fetch(
        Constants.baseURL + Constants.apiGetSvLinkStatus,
        {
          credentials: 'include'
        }
      );

      if (!statusResponse.ok) {
        throw new Error('Failed to reload');
      }

      const data = await statusResponse.json();

      this.setState({
        saving: false,
        status: data.status,
        onDateTime: data.onDateTime,
        offDateTime: data.offDateTime
      });

    } catch (e) {

      console.error(e);

      this.setState((prev) => ({
        saving: false,
        status: !prev.status
      }));
    }
  };

  formatDate = (date) => {
    if (!date) return 'Never';

    return new Date(date + 'Z').toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }) + ' AEST';
  };

  render() {

    const {
      loading,
      saving,
      status,
      onDateTime,
      offDateTime
    } = this.state;

    if (loading) {
      return (
        <Box
          sx={{
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Fade in timeout={400}>
        <Box>

          <Title>SV-Link</Title>

          <Typography
            variant="body2"
            sx={{
              opacity: 0.7,
              mb: 4
            }}
          >
            Enable or Disable TCP connectivity.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 8,
              background:
                status
                  ? 'linear-gradient(135deg, rgba(46,125,50,0.22), rgba(27,94,32,0.08))'
                  : 'linear-gradient(135deg, rgba(198,40,40,0.20), rgba(90,20,20,0.08))',

              border: status
                ? '1px solid rgba(76,175,80,0.35)'
                : '1px solid rgba(244,67,54,0.30)',

              backdropFilter: 'blur(12px)',
              transition: 'all 0.25s ease'
            }}
          >

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={3}
            >

              <Box>

                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <PowerSettingsNewRoundedIcon
                    sx={{
                      fontSize: 28,
                      color: status ? '#66bb6a' : '#ef5350'
                    }}
                  />

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500
                    }}
                  >
                    {'SV-Link Connection'}
                  </Typography>
                </Stack>

                <Chip
                  label={status ? 'ENABLED' : 'DISABLED'}
                  sx={{
                    fontWeight: 400,
                    letterSpacing: 1,
                    bgcolor: status
                      ? 'rgba(76,175,80,0.15)'
                      : 'rgba(244,67,54,0.15)',

                    color: status
                      ? '#81c784'
                      : '#ef9a9a'
                  }}
                />

              </Box>

              <Box
                sx={{
                  position: 'relative'
                }}
              >
                <Switch
                  checked={status}
                  disabled={saving}
                  onChange={(e) => this.updateStatus(e.target.checked)}
                  size="medium"
                  sx={{
                    transform: 'scale(1.2)',

                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50'
                    },

                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4caf50'
                    }
                  }}
                />

                {saving && (
                  <CircularProgress
                    size={54}
                    sx={{
                      position: 'absolute',
                      top: -5,
                      left: -5
                    }}
                  />
                )}
              </Box>

            </Stack>

          </Paper>

          <Stack
            spacing={2}
            sx={{
              mt: 4
            }}
          >

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 4,
                borderRadius: 8,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
            >

              <Stack direction="row" spacing={2} alignItems="center">

                <AccessTimeRoundedIcon sx={{ opacity: 0.7 }} />

                <Box>

                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.6
                    }}
                  >
                    {status ? 'Enabled DateTime' : 'Disabled DateTime'}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500
                    }}
                  >
                    {status
                      ? this.formatDate(onDateTime)
                      : this.formatDate(offDateTime)}
                  </Typography>

                </Box>

              </Stack>

            </Paper>

          </Stack>

        </Box>
      </Fade>
    );
  }
}